/**
 * Cookie Service
 * 
 * NOTA IMPORTANTE: Las cookies HttpOnly solo pueden ser establecidas por el servidor.
 * En el frontend, JavaScript no puede establecer cookies HttpOnly por razones de seguridad.
 * 
 * Para una implementación completa de HttpOnly:
 * 1. El backend debe establecer la cookie HttpOnly cuando se obtiene el refresh token
 * 2. El frontend solo puede leer/eliminar cookies HttpOnly mediante peticiones al backend
 * 
 * Esta implementación usa cookies normales con Secure y SameSite para máxima seguridad
 * posible desde el frontend. Para HttpOnly real, el backend debe manejar las cookies.
 */

export interface CookieOptions {
  expires?: Date | number; // Días o fecha específica
  maxAge?: number; // Segundos
  path?: string;
  domain?: string;
  secure?: boolean; // Solo HTTPS
  sameSite?: 'Strict' | 'Lax' | 'None';
  httpOnly?: boolean; // Nota: No funciona desde JS, solo para documentación
}

class CookieService {
  /**
   * Establece una cookie
   * @param name - Nombre de la cookie
   * @param value - Valor de la cookie
   * @param options - Opciones de la cookie
   */
  set(name: string, value: string, options: CookieOptions = {}): void {
    if (typeof document === 'undefined') {
      console.warn('CookieService: document no está disponible (SSR)');
      return;
    }

    const {
      expires,
      maxAge,
      path = '/',
      domain,
      secure = true, // Por defecto solo HTTPS en producción
      sameSite = 'Lax', // Protección CSRF
    } = options;

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    // Expires
    if (expires) {
      const expiryDate = typeof expires === 'number' 
        ? new Date(Date.now() + expires * 24 * 60 * 60 * 1000) // Días a milisegundos
        : expires;
      cookieString += `; expires=${expiryDate.toUTCString()}`;
    }

    // Max-Age (tiene prioridad sobre expires)
    if (maxAge) {
      cookieString += `; max-age=${maxAge}`;
    }

    // Path
    cookieString += `; path=${path}`;

    // Domain
    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    // Secure (solo HTTPS)
    if (secure) {
      cookieString += `; secure`;
    }

    // SameSite
    cookieString += `; samesite=${sameSite}`;

    // HttpOnly - NOTA: No se puede establecer desde JavaScript
    // Solo se puede establecer desde el servidor
    if (options.httpOnly) {
      console.warn(
        'CookieService: httpOnly no se puede establecer desde JavaScript. ' +
        'El backend debe establecer la cookie HttpOnly.'
      );
    }

    document.cookie = cookieString;
  }

  /**
   * Obtiene el valor de una cookie
   * @param name - Nombre de la cookie
   * @returns El valor de la cookie o null si no existe
   */
  get(name: string): string | null {
    if (typeof document === 'undefined') {
      return null;
    }

    const nameEQ = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }

    return null;
  }

  /**
   * Elimina una cookie
   * @param name - Nombre de la cookie
   * @param options - Opciones (path y domain deben coincidir con los usados al crear)
   */
  remove(name: string, options: { path?: string; domain?: string } = {}): void {
    const { path = '/', domain } = options;
    
    // Establecer la cookie con fecha de expiración en el pasado
    let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
    
    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (typeof document !== 'undefined') {
      document.cookie = cookieString;
    }
  }

  /**
   * Verifica si una cookie existe
   * @param name - Nombre de la cookie
   * @returns true si la cookie existe
   */
  has(name: string): boolean {
    return this.get(name) !== null;
  }

  /**
   * Obtiene todas las cookies como un objeto
   * @returns Objeto con todas las cookies
   */
  getAll(): Record<string, string> {
    if (typeof document === 'undefined') {
      return {};
    }

    const cookies: Record<string, string> = {};
    const cookieArray = document.cookie.split(';');

    for (const cookie of cookieArray) {
      const [name, value] = cookie.trim().split('=');
      if (name && value) {
        cookies[decodeURIComponent(name)] = decodeURIComponent(value);
      }
    }

    return cookies;
  }
}

// Singleton instance
export const cookieService = new CookieService();

